import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
   copyFormStructure,
   editFormStructure,
   getFormsList,
   getFormStructure,
   patchFormMethods,
   saveFormStructure,
} from "../../../services/restAPI/FormsV3";
import { Message } from "../../BP2Components";
import { useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import TextInput from "./Inputs/TextInput";
import { fieldMapper } from "./FormPreview/fieldMapper";

export const DECORATORS = {
   edit: {
      name: "EditableDecorator",
      properties: {
         edit: true,
      },
   },
   required: {
      name: "RequiredDecorator",
      properties: {
         required: false,
      },
   },
   hide: {
      name: "HideFieldDecorator",
      properties: {
         hide: false,
      },
   },
   position: {
      name: "PositionDecorator",
      properties: {
         position: "",
      },
   },
   ViewOptionsDecorator: {
      name: "ViewOptionsDecorator",
      properties: {
         fieldsView: "",
      },
   },
   valueFormat: {
      name: "ValueFormatDecorator",
      properties: {
         format: "",
      },
   },
};

const FormBuilderContext = React.createContext();

const defaultTabLabel = (tabId) => `Tab ${Number(tabId) + 1}`;

const initialData = {
   name: "",
   formSelected: null,
   description: "",
};

function dataReducer(state, action) {
   switch (action.type) {
      case "change": {
         return { ...state, ...action.value };
      }

      default: {
         throw new Error(`Unhandled action type: ${action.type}`);
      }
   }
}

function FormBuilderProvider({ children }) {
   const intl = useIntl();
   const [selectedForm, setSelectedForm] = useState();
   const [editedForm, setEditedForm] = useState();
   const [previewMode, setPrevieMode] = useState(false);
   const [data, setData] = React.useReducer(dataReducer, initialData);
   const [loadingForms, setLoadingForms] = useState(false);
   const [formItems, setFormItems] = useState([]);
   const [listTableData, setListTableData] = useState([
      { listTableId: null, listId: null, name: "" },
   ]);
   const [businessMethods, setBusinessMethods] = useState([]);
   const [showSaveButton, setShowSaveButton] = useState(true);
   const [formOptions, setFormOptions] = useState({});

   // Flat (no-tabs) field list
   const [_listItems, _setListItems] = useState([]);

   // Tabs state
   // tabs: [{ uuid, id (string index "0","1"...), name, sections }]
   const [tabs, setTabs] = useState([]);
   const [tabsItems, setTabsItems] = useState({}); // { [tabId]: items[] }
   const [activeTabId, setActiveTabId] = useState(null);

   const hasTabs = tabs.length > 0;
   const hasMultipleTabs = tabs.length > 1;

   const addTab = useCallback(() => {
      const nextId = String(
         tabs.length > 0 ? Math.max(...tabs.map((tab) => Number(tab.id))) + 1 : 0,
      );
      const newTab = {
         uuid: uuidv4(),
         id: nextId,
         label: defaultTabLabel(nextId),
         sections: { 0: { label: "" } },
      };
      // When adding the first tab, migrate existing flat fields into it instead of starting empty
      const isFirstTab = tabs.length === 0;
      setTabs((prev) => [...prev, newTab]);
      setTabsItems((prev) => ({ ...prev, [nextId]: isFirstTab ? _listItems : [] }));
      if (isFirstTab) _setListItems([]);
      setActiveTabId(nextId);
      return { id: nextId, label: newTab.label };
   }, [tabs, _listItems]);

   const renameTab = useCallback((tabId, newLabel) => {
      setTabs((prev) =>
         prev.map((tab) =>
            tab.id === tabId ? { ...tab, label: newLabel.trim() || defaultTabLabel(tabId) } : tab,
         ),
      );
   }, []);

   const removeTab = useCallback(
      (tabId) => {
         // Removing the last tab: migrate its fields back to flat mode
         if (tabs.length === 1) {
            _setListItems(tabsItems[tabId] ?? []);
            setTabs([]);
            setTabsItems({});
            setActiveTabId(null);
            return;
         }

         setTabs((prev) => {
            const next = prev.filter((tab) => tab.id !== tabId);
            if (activeTabId === tabId) {
               const removedIndex = prev.findIndex((tab) => tab.id === tabId);
               const fallback = next[removedIndex - 1] ?? next[0] ?? null;
               setActiveTabId(fallback?.id ?? null);
            }
            return next;
         });
         setTabsItems((prev) => {
            const next = { ...prev };
            delete next[tabId];
            return next;
         });
      },
      [activeTabId, tabs, tabsItems],
   );

   // Tab-aware listItems / setListItems — all consumers use these transparently
   const listItems = hasTabs ? (tabsItems[activeTabId] ?? []) : _listItems;

   const setListItems = useCallback(
      (updater) => {
         if (hasTabs && activeTabId !== null) {
            setTabsItems((prev) => {
               const current = prev[activeTabId] ?? [];
               const next = typeof updater === "function" ? updater(current) : updater;
               return { ...prev, [activeTabId]: next };
            });
         } else {
            _setListItems(updater);
         }
      },
      [hasTabs, activeTabId],
   );

   const fetchForms = async () => {
      setLoadingForms(true);
      try {
         const res = await getFormsList();
         setFormItems(res.data);
      } catch (error) {
         console.error("getFormsList failed", error);
         setFormItems([]);
      } finally {
         setLoadingForms(false);
      }
   };

   const normalizeFieldType = (item) => {
      //FROM FORM
      if (typeof item?.type === "string" && item.type.includes(".")) {
         const [module, name] = (item.type || "").split(".");
         return { module, name, isPrimitive: module === "primitives" };
      }
      // FROM BACKEND
      if (item?.modelField?.type?.module && item?.modelField?.type?.name) {
         return {
            module: item.modelField.type.module,
            name: item.modelField.type.name,
            isPrimitive: item.modelField.type.isPrimitive,
         };
      }
      return { module: "", name: "" };
   };

   const normalizeConstraints = (item) => item?.constraints ?? item?.modelField?.constraints ?? [];

   const normalizeMultiplicity = (item) =>
      item?.multiplicity ?? item?.modelField?.multiplicity ?? "SINGULAR";

   const normalizeFieldName = (item) => item?.name ?? item?.modelField?.name;

   const getPosition = (itm) =>
      itm?.decorators?.find?.((d) => d?.name === "PositionDecorator")?.properties?.position ?? "";

   const combineColumnsFromFlat = (items) => {
      const groups = items.reduce((acc, item) => {
         const order = item.displaySort ?? 0;
         acc[order] = acc[order] || [];
         acc[order].push(item);
         return acc;
      }, {});

      const sorts = Object.keys(groups)
         .map(Number)
         .sort((a, b) => a - b);

      const result = [];
      const used = new Set();

      for (const order of sorts) {
         const arr = groups[order];

         const left = arr.find((field) => getPosition(field) === "left");
         const right = arr.find((field) => getPosition(field) === "right");

         if (left || right) {
            if (left) used.add(left.uuid);
            if (right) used.add(right.uuid);

            result.push({
               uuid: uuidv4(),
               type: "columns",
               displaySort: order,
               items: [left || null, right || null],
            });
         }

         for (const item of arr) {
            if (!used.has(item.uuid)) result.push(item);
         }
      }

      return result.sort((a, b) => (a.displaySort ?? 0) - (b.displaySort ?? 0));
   };

   const normalizeDisplaySorts = (items) => {
      const clone = items.map((item) => ({ ...item }));
      const visitedFlatSorts = new Set();
      let nextSort = 1;

      for (let i = 0; i < clone.length; i++) {
         const item = clone[i];

         if (item?.type === "columns") {
            const rowSort = nextSort++;
            const newItems = Array.isArray(item.items)
               ? item.items.map((field) => (field ? { ...field, displaySort: rowSort } : field))
               : item.items;
            clone[i] = { ...item, displaySort: rowSort, items: newItems };
            continue;
         }

         const position = getPosition(item);
         if (position) {
            const oldSort = item.displaySort ?? i + 1;
            if (visitedFlatSorts.has(oldSort)) {
               continue;
            }
            visitedFlatSorts.add(oldSort);

            const rowSort = nextSort++;

            for (let j = 0; j < clone.length; j++) {
               const field = clone[j];
               if (!field) continue;
               const fieldPos = getPosition(field);
               const fieldOldSort = field.displaySort ?? j + 1;
               if (fieldPos && fieldOldSort === oldSort) {
                  clone[j] = { ...field, displaySort: rowSort };
               }
            }
            continue;
         }

         const rowSort = nextSort++;
         clone[i] = { ...item, displaySort: rowSort };
      }

      return clone;
   };

   const isValidFieldForPayload = (field) => {
      if (!field) return false;
      const name = normalizeFieldName(field);
      const type = normalizeFieldType(field);
      const hasType = !!(type?.module || type?.name);
      return !!name || hasType;
   };

   // Empty fieldsView/position/format sent to backend causes it to clear the displayed value.
   const filterDecorators = (decorators) =>
      (decorators ?? []).filter((d) => {
         if (d.name === DECORATORS.ViewOptionsDecorator.name) return !!d.properties?.fieldsView;
         if (d.name === DECORATORS.position.name) return !!d.properties?.position;
         if (d.name === DECORATORS.valueFormat.name) return !!d.properties?.format;
         return true;
      });

   // Builds the flat fields array from a list of items
   const buildFieldsList = (items) => {
      return (items ?? []).flatMap((item, index) => {
         if (
            item?.modelField?.type?.module === "primitives" &&
            item?.modelField?.type?.name === "ListTable"
         ) {
            return [
               {
                  label: item.label,
                  hid: item.hid,
                  placeholder: item.placeholder,
                  decorators: filterDecorators(item.decorators),
                  displaySort: item.displaySort ?? index + 1,
                  sectionIndex: item?.sectionIndex,
                  modelField: {
                     ...item.modelField,
                  },
               },
            ];
         }
         if (item.type === "columns") {
            const validSubItems = (item.items ?? []).filter(isValidFieldForPayload);
            if (!validSubItems.length) return [];

            return validSubItems.map((subItem) => {
               const fieldType = normalizeFieldType(subItem);
               const constraints = normalizeConstraints(subItem);
               const multiplicity = normalizeMultiplicity(subItem);
               const fieldName = normalizeFieldName(subItem);
               const fieldLabel = subItem?.label;

               return {
                  label: fieldLabel,
                  hid: subItem.hid,
                  placeholder: subItem.placeholder,
                  decorators: filterDecorators(subItem.decorators ?? item.decorators),
                  displaySort: subItem.displaySort ?? item.displaySort ?? index + 1,
                  sectionIndex: subItem?.sectionIndex ?? item?.sectionIndex,
                  modelField: {
                     name: fieldName,
                     label: fieldLabel,
                     type: fieldType,
                     constraints,
                     multiplicity,
                  },
               };
            });
         }

         if (item.type === "section") {
            return [
               {
                  label: item.label ?? "",
                  decorators: filterDecorators(Object.values(DECORATORS)),
                  displaySort: item.displaySort ?? index + 1,
                  sectionIndex: item?.sectionIndex ?? index,
                  modelField: {
                     name: "",
                     type: { module: "primitives", name: "Section", isPrimitive: true },
                     constraints: [],
                     multiplicity: "SINGULAR",
                  },
               },
            ];
         }

         if (!isValidFieldForPayload(item)) return [];

         const fieldType = normalizeFieldType(item);
         const constraints = normalizeConstraints(item);
         const multiplicity = normalizeMultiplicity(item);
         const fieldName = normalizeFieldName(item);
         const fieldLabel = item?.label;

         return [
            {
               label: fieldLabel,
               hid: item.hid,
               id: item.id,
               placeholder: item.placeholder,
               decorators: filterDecorators(item.decorators),
               displaySort: item.displaySort ?? index + 1,
               sectionIndex: item?.sectionIndex,
               modelField: {
                  name: fieldName,
                  label: fieldLabel,
                  type: fieldType,
                  constraints,
                  multiplicity,
               },
            },
         ];
      });
   };

   const buildFormData = (data) => {
      const rawType =
         data?.formSelected?.value ??
         (typeof selectedForm === "string" ? selectedForm : selectedForm?.value) ??
         "";
      const [typeModule, typeName] = rawType.split("-");

      if (hasTabs) {
         // Build all fields from all tabs, each tagged with tabIndex
         const allFields = tabs.flatMap((tab) => {
            const normalized = normalizeDisplaySorts(tabsItems[tab.id] ?? []);
            return buildFieldsList(normalized).map((field) => ({
               ...field,
               tabIndex: Number(tab.id),
               sectionIndex: field.sectionIndex ?? 0,
            }));
         });

         // Reconstruct tabs object (keyed by string index) preserving sections
         const tabsPayload = {};
         for (const tab of tabs) {
            tabsPayload[tab.id] = {
               label: tab.label ?? "",
               sections: tab.sections ?? { 0: { label: "" } },
            };
         }

         return {
            id: data?.id,
            name: data?.name ?? "",
            type: { module: typeModule, name: typeName },
            fields: allFields,
            tabs: tabsPayload,
            formOptions: { ...formOptions, showSave: showSaveButton },
         };
      }

      const normalizedItems = normalizeDisplaySorts(listItems);
      const fields = buildFieldsList(normalizedItems);

      return {
         id: data?.id,
         name: data?.name ?? "",
         type: { module: typeModule, name: typeName },
         fields,
         formOptions: { ...formOptions, showSave: showSaveButton },
      };
   };

   const patchFormMethodsSafely = async (formId, currentMethods) => {
      try {
         await patchFormMethods(formId, currentMethods);
         return true;
      } catch (error) {
         console.error("patchFormMethods failed", error);
         Message.error(intl.formatMessage({ id: "forms.saveError" }));
         return false;
      }
   };

   const submitForm = async () => {
      if (!data.formSelected && !selectedForm) return;

      const formData = buildFormData(data);
      if (!formData?.type?.module || !formData?.type?.name) {
         console.warn("Invalid form type", formData?.type);
         return;
      }
      try {
         if (!!data.id) {
            const result = await editFormStructure(formData, data.id);
            const response = result.data;
            const methodsOk = await patchFormMethodsSafely(response.id, businessMethods);
            fetchForm(response.id);
            if (result.status === 200 && methodsOk) {
               Message.success(intl.formatMessage({ id: "forms.editSuccess" }));
            }
         } else {
            delete formData.id;
            const result = await saveFormStructure(formData);
            if (result.status === 200) {
               const response = result.data;
               setData({ type: "change", value: { ...data, id: response.id } });
               const methodsOk = await patchFormMethodsSafely(response.id, businessMethods);
               fetchForm(response.id);
               if (methodsOk) {
                  Message.success(intl.formatMessage({ id: "forms.addSuccess" }));
               }
            }
         }
      } catch (error) {
         console.error("submitForm failed", error);
         Message.error(intl.formatMessage({ id: "forms.saveError" }));
      } finally {
         await fetchForms();
      }
   };

   const mergeDecorators = (defaultsDecorators = [], overridesDecorators = []) => {
      const mapOfDecorators = new Map(
         defaultsDecorators.map((defaultsDecorator) => [
            defaultsDecorator.name,
            { ...defaultsDecorator, properties: { ...(defaultsDecorator.properties || {}) } },
         ]),
      );

      (overridesDecorators || []).forEach((overridesDecorator) => {
         const name = overridesDecorator?.name;
         if (!name) return;

         const base = mapOfDecorators.get(name);
         if (base) {
            // merge properties in decorators
            const merged = { ...base, ...overridesDecorator };
            merged.properties = { ...base.properties, ...overridesDecorator.properties };

            mapOfDecorators.set(name, merged);
         }
      });

      return Array.from(mapOfDecorators.values());
   };

   // Converts a raw API fields array into processed listItems format
   const processFields = (fields) => {
      const inputs = (fields ?? []).map((field, idx) => {
         const { module, name } = normalizeFieldType(field);
         const typeKey = `${module}.${name}`;
         const label = field?.label ?? field?.modelField?.label ?? "";
         const defaultDecorators = Object.values(DECORATORS);
         const decorators = mergeDecorators(defaultDecorators, field.decorators);
         return {
            uuid: uuidv4(),
            ...field,
            hid: field.hid,
            decorators,
            label,
            name: normalizeFieldName(field),
            Input: fieldMapper[typeKey] || TextInput,
            displaySort: field.displaySort ?? idx + 1,
         };
      });

      const sortedInputs = inputs.sort((a, b) => a.displaySort - b.displaySort);
      return combineColumnsFromFlat(sortedInputs);
   };

   const fetchForm = async (currentId) => {
      const id = currentId ?? editedForm;
      if (!id) {
         setFormOptions({});
         setBusinessMethods([]);
         setShowSaveButton(true);
         return;
      }

      setEditedForm(id);
      const res = await getFormStructure(id);
      if (!res?.data) return;

      const data = res.data;

      // tabs is an object keyed by string index: { "0": { label, sections }, "1": {...} }
      const rawTabs = data.tabs;
      const tabKeys = rawTabs ? Object.keys(rawTabs).sort((a, b) => Number(a) - Number(b)) : [];

      if (tabKeys.length > 0) {
         const parsedTabs = tabKeys.map((key) => ({
            uuid: uuidv4(),
            id: key,
            label: rawTabs[key].label || defaultTabLabel(key),
            sections: rawTabs[key].sections,
         }));
         setTabs(parsedTabs);

         // Group fields by tabIndex in a single pass, then process each tab
         const fieldsByTabId = (data.fields ?? []).reduce((acc, field) => {
            const tabId = String(field.tabIndex ?? 0);
            if (!acc[tabId]) acc[tabId] = [];
            acc[tabId].push(field);
            return acc;
         }, {});

         const newTabsItems = {};
         for (const tab of parsedTabs) {
            newTabsItems[tab.id] = processFields(fieldsByTabId[tab.id] ?? []);
         }
         setTabsItems(newTabsItems);
         setActiveTabId(parsedTabs[0]?.id ?? null);
         _setListItems([]);
      } else {
         setTabs([]);
         setTabsItems({});
         setActiveTabId(null);
         _setListItems(processFields(data.fields));
      }

      const formSelected = {
         value: `${data?.type?.module}-${data?.type?.name}`,
         label: data?.type?.name,
      };
      setSelectedForm(formSelected);
      setBusinessMethods(data.formActions ?? []);
      setFormOptions(data.formOptions ?? {});
      setShowSaveButton(data.formOptions?.showSave ?? true);

      setData({
         type: "change",
         value: {
            id: data.id,
            name: data.name,
            formSelected,
         },
      });
   };

   const submitFormMethods = async (methods) => {
      if (!data.id) {
         Message.error(
            intl.formatMessage({
               id: "forms.methodsSaveError",
               defaultMessage: "Błąd zapisu metod",
            }),
         );
         return;
      }
      if (JSON.stringify(methods) === JSON.stringify(businessMethods)) return;
      try {
         await patchFormMethods(data.id, methods);
         setBusinessMethods(methods);
         Message.success(
            intl.formatMessage({
               id: "forms.methodsSaveSuccess",
               defaultMessage: "Metody zapisane",
            }),
         );
      } catch (error) {
         console.error("patchFormMethods failed", error);
         Message.error(
            intl.formatMessage({
               id: "forms.methodsSaveError",
               defaultMessage: "Błąd zapisu metod",
            }),
         );
         throw error;
      }
   };

   const copyForm = async (id) => {
      try {
         const result = await copyFormStructure(id);
         if (result.status === 200) {
            Message.success(intl.formatMessage({ id: "forms.copySuccess" }));
            await fetchForms();
         } else {
            Message.error(intl.formatMessage({ id: "forms.copyError" }));
         }
      } catch (error) {
         console.error("copyFormStructure failed", error);
      }
   };

   useEffect(() => {
      fetchForm();
   }, [editedForm]);

   useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const formId = params.get("formId");
      if (formId) {
         setEditedForm(formId);
      }
   }, []);

   const updateItem = (uuid, updater) => {
      setListItems((prev) => {
         const next = [...prev];

         const recurse = (arr) => {
            return arr.map((el) => {
               if (!el) return el;

               if (el.uuid === uuid) {
                  return updater(el);
               }

               if (el.type === "columns" && Array.isArray(el.items)) {
                  return { ...el, items: recurse(el.items) };
               }

               return el;
            });
         };

         return recurse(next);
      });
   };

   return (
      <FormBuilderContext.Provider
         value={{
            previewMode,
            setPrevieMode,
            data,
            setData,
            submitForm,
            fetchForms,
            copyForm,
            loadingForms,
            formItems,
            listItems,
            setListItems,
            editedForm,
            setEditedForm,
            selectedForm,
            setSelectedForm,
            updateItem,
            listTableData,
            setListTableData,
            businessMethods,
            setBusinessMethods,
            showSaveButton,
            setShowSaveButton,
            submitFormMethods,
            // Tabs
            tabs,
            setTabs,
            tabsItems,
            setTabsItems,
            activeTabId,
            setActiveTabId,
            hasTabs,
            hasMultipleTabs,
            addTab,
            renameTab,
            removeTab,
         }}
      >
         {children}
      </FormBuilderContext.Provider>
   );
}

export { FormBuilderProvider, FormBuilderContext };
